// src/prototype/utils/weighting.ts
// ============================================================
// WEIGHTING UTILITIES
// Normalization and calculation functions for category and measure weights
// 
// UPDATED: January 2026 - Golden Demo Integration
// - calculateWeightedScore now handles ROI in raw numbers (not millions)
// - $5,000,000 raw = 100 score (was: 5 = 100)
// ============================================================

// ============================================================
// TYPES
// ============================================================

export interface CategoryWeight {
  id: string;
  name: string;
  shortName: string;
  weight: number; // 0-100 percentage
  dimension: 'BV' | 'EOI'; // Business Value or Ease of Implementation
  description: string;
}

export interface MeasureWeight {
  id: string;
  name: string;
  categoryId: string;
  weight: number; // 0-100 percentage within category
  description: string;
}

export type CategoryWeightsMap = Record<string, number>;
export type MeasureWeightsMap = Record<string, Record<string, number>>;

function distributeRoundedWeights(
  weights: Record<string, number>,
  targetTotal: number
): Record<string, number> {
  const target = Math.round(targetTotal);
  const entries = Object.entries(weights);

  if (entries.length === 0) {
    return {};
  }

  const flooredEntries = entries.map(([key, value]) => ({
    key,
    floor: Math.floor(value),
    remainder: value - Math.floor(value),
  }));

  let remainderToDistribute = target - flooredEntries.reduce((sum, entry) => sum + entry.floor, 0);

  flooredEntries
    .sort((a, b) => {
      if (b.remainder !== a.remainder) {
        return b.remainder - a.remainder;
      }
      return entries.findIndex(([key]) => key === a.key) - entries.findIndex(([key]) => key === b.key);
    })
    .forEach((entry) => {
      if (remainderToDistribute <= 0) {
        return;
      }

      entry.floor += 1;
      remainderToDistribute -= 1;
    });

  flooredEntries.sort(
    (a, b) => entries.findIndex(([key]) => key === a.key) - entries.findIndex(([key]) => key === b.key)
  );

  return flooredEntries.reduce<Record<string, number>>((acc, entry) => {
    acc[entry.key] = entry.floor;
    return acc;
  }, {});
}

// ============================================================
// LUCIDE ICON IMPORTS (for component usage)
// ============================================================
import {
  TrendingUp,
  Target,
  Clock,
  Database,
  Shield,
  Users,
  type LucideIcon,
} from 'lucide-react';

// ============================================================
// CATEGORY DEFINITIONS (with icons for UI)
// ============================================================

export interface CategoryDefinition {
  label: string;
  shortLabel: string;
  description: string;
  dimension: 'Business Value' | 'Ease of Implementation';
  icon: LucideIcon;
  defaultWeight: number;
}

export const CATEGORY_DEFINITIONS: Record<string, CategoryDefinition> = {
  costSavings: {
    label: 'Revenue Potential & Cost Savings',
    shortLabel: 'Revenue & Cost',
    description: 'Direct financial impact from implementation',
    dimension: 'Business Value',
    icon: TrendingUp,
    defaultWeight: 25,
  },
  strategicAlignment: {
    label: 'Alignment with Strategic Goals',
    shortLabel: 'Strategic Alignment',
    description: 'Fit with organizational mission and goals',
    dimension: 'Business Value',
    icon: Target,
    defaultWeight: 20,
  },
  timeToValue: {
    label: 'Time-to-Value',
    shortLabel: 'Time-to-Value',
    description: 'Speed to realize benefits',
    dimension: 'Business Value',
    icon: Clock,
    defaultWeight: 10,
  },
  dataReadiness: {
    label: 'Data Readiness and Availability',
    shortLabel: 'Data Readiness',
    description: 'Quality and accessibility of required data',
    dimension: 'Ease of Implementation',
    icon: Database,
    defaultWeight: 15,
  },
  compliance: {
    label: 'Regulatory and Compliance Considerations',
    shortLabel: 'Regulatory Compliance',
    description: 'Regulatory alignment and risk factors',
    dimension: 'Ease of Implementation',
    icon: Shield,
    defaultWeight: 15,
  },
  orgReadiness: {
    label: 'Organizational Readiness and Capabilities',
    shortLabel: 'Org Readiness',
    description: 'Staff expertise and change management capacity',
    dimension: 'Ease of Implementation',
    icon: Users,
    defaultWeight: 15,
  },
};

// ============================================================
// MEASURE DEFINITIONS (for UI display)
// ============================================================

export interface MeasureDefinition {
  key: string;
  label: string;
  description: string;
  defaultWeight: number;
}

export const MEASURE_DEFINITIONS: Record<string, MeasureDefinition[]> = {
  costSavings: [
    { key: 'revenueGeneration', label: 'Revenue Generation', description: 'Potential for new revenue streams', defaultWeight: 25 },
    { key: 'costSavings', label: 'Cost Savings', description: 'Direct cost reduction opportunities', defaultWeight: 25 },
    { key: 'longTermValue', label: 'Long-Term Value Creation', description: 'Sustainable competitive advantage', defaultWeight: 25 },
    { key: 'financialRisk', label: 'Financial Risk Factors', description: 'Investment risk considerations', defaultWeight: 25 },
  ],
  strategicAlignment: [
    { key: 'coreMission', label: 'Core Mission Support', description: 'Alignment with organizational mission', defaultWeight: 20 },
    { key: 'competitive', label: 'Competitive Differentiation', description: 'Market positioning advantage', defaultWeight: 10 },
    { key: 'valueBased', label: 'Value-Based Care Integration', description: 'Support for value-based models', defaultWeight: 10 },
    { key: 'strategicGrowth', label: 'Strategic Growth', description: 'Long-term viability and growth', defaultWeight: 20 },
    { key: 'operationalEfficiency', label: 'Operational Efficiency', description: 'Process improvement potential', defaultWeight: 20 },
    { key: 'techAlignment', label: 'Technology Alignment', description: 'Fit with technology roadmap', defaultWeight: 10 },
    { key: 'revenueDiversification', label: 'Revenue Diversification', description: 'New revenue stream potential', defaultWeight: 10 },
  ],
  timeToValue: [
    { key: 'implementationComplexity', label: 'Implementation Complexity', description: 'Technical and operational complexity', defaultWeight: 17 },
    { key: 'marketReadiness', label: 'Market Readiness', description: 'External market conditions', defaultWeight: 17 },
    { key: 'speedToReturns', label: 'Speed to Generate Returns', description: 'Time to ROI realization', defaultWeight: 17 },
    { key: 'stakeholderEngagement', label: 'Stakeholder Engagement', description: 'Stakeholder buy-in requirements', defaultWeight: 17 },
    { key: 'pilotFeasibility', label: 'Pilot Feasibility', description: 'Ability to run controlled pilots', defaultWeight: 16 },
    { key: 'regulatorySpeed', label: 'Regulatory Approval Speed', description: 'Time for compliance approvals', defaultWeight: 16 },
  ],
  dataReadiness: [
    { key: 'dataQuality', label: 'Data Quality', description: 'Completeness and accuracy of data', defaultWeight: 14 },
    { key: 'dataStandardization', label: 'Data Standardization', description: 'Consistency of data formats', defaultWeight: 14 },
    { key: 'dataAccessibility', label: 'Data Accessibility', description: 'Ease of data access and integration', defaultWeight: 14 },
    { key: 'dataTransformation', label: 'Data Transformation', description: 'Cleansing requirements', defaultWeight: 14 },
    { key: 'interoperability', label: 'Interoperability', description: 'System connectivity', defaultWeight: 14 },
    { key: 'historicalData', label: 'Historical Data', description: 'Availability of historical records', defaultWeight: 15 },
    { key: 'realTime', label: 'Real-Time Capabilities', description: 'Live data feed availability', defaultWeight: 15 },
  ],
  compliance: [
    { key: 'securityPrivacy', label: 'Security & Privacy', description: 'HIPAA and data security compliance', defaultWeight: 12 },
    { key: 'deIdentification', label: 'De-Identification', description: 'Anonymization requirements', defaultWeight: 12 },
    { key: 'regulatoryFramework', label: 'Regulatory Framework', description: 'Alignment with regulations', defaultWeight: 13 },
    { key: 'legalClarity', label: 'Legal Clarity', description: 'Contractual requirements', defaultWeight: 13 },
    { key: 'auditability', label: 'Auditability', description: 'Reporting and audit requirements', defaultWeight: 12 },
    { key: 'vendorCompliance', label: 'Vendor Compliance', description: 'Third-party compliance', defaultWeight: 12 },
    { key: 'jurisdictional', label: 'Jurisdictional Complexity', description: 'Multi-state/region considerations', defaultWeight: 13 },
    { key: 'patientConsent', label: 'Patient Consent', description: 'Consent management requirements', defaultWeight: 13 },
  ],
  orgReadiness: [
    { key: 'techInfra', label: 'Technical Infrastructure', description: 'IT systems and platforms', defaultWeight: 15 },
    { key: 'staffExpertise', label: 'Staff Expertise', description: 'Team skills and knowledge', defaultWeight: 15 },
    { key: 'orgProcesses', label: 'Organizational Processes', description: 'Workflow readiness', defaultWeight: 15 },
    { key: 'changeManagement', label: 'Change Management', description: 'Adoption capacity', defaultWeight: 15 },
    { key: 'resourceAllocation', label: 'Resource Allocation', description: 'Budget and staff availability', defaultWeight: 25 },
    { key: 'partnerships', label: 'Partnerships', description: 'External collaboration readiness', defaultWeight: 15 },
  ],
};

// ============================================================
// DEFAULT CATEGORY WEIGHTS (legacy format - kept for compatibility)
// ============================================================

export const DEFAULT_CATEGORY_WEIGHTS: CategoryWeight[] = [
  {
    id: 'costSavings',
    name: 'Revenue Potential & Cost Savings',
    shortName: 'Revenue & Cost',
    weight: 25,
    dimension: 'BV',
    description: 'Direct financial impact from implementation',
  },
  {
    id: 'strategicAlignment',
    name: 'Alignment with Strategic Goals',
    shortName: 'Strategic Alignment',
    weight: 20,
    dimension: 'BV',
    description: 'Fit with organizational mission and goals',
  },
  {
    id: 'timeToValue',
    name: 'Time-to-Value',
    shortName: 'Time-to-Value',
    weight: 10,
    dimension: 'BV',
    description: 'Speed to realize benefits',
  },
  {
    id: 'dataReadiness',
    name: 'Data Readiness and Availability',
    shortName: 'Data Readiness',
    weight: 15,
    dimension: 'EOI',
    description: 'Quality and accessibility of required data',
  },
  {
    id: 'compliance',
    name: 'Regulatory and Compliance Considerations',
    shortName: 'Regulatory Compliance',
    weight: 15,
    dimension: 'EOI',
    description: 'Regulatory alignment and risk factors',
  },
  {
    id: 'orgReadiness',
    name: 'Organizational Readiness and Capabilities',
    shortName: 'Org Readiness',
    weight: 15,
    dimension: 'EOI',
    description: 'Staff expertise and change management capacity',
  },
];

// ============================================================
// DEFAULT MEASURE WEIGHTS (by category)
// ============================================================

export const DEFAULT_MEASURE_WEIGHTS: Record<string, MeasureWeight[]> = {
  costSavings: [
    { id: 'm1', name: 'Revenue Generation', categoryId: 'costSavings', weight: 25, description: 'Potential for new revenue streams' },
    { id: 'm2', name: 'Cost Savings', categoryId: 'costSavings', weight: 25, description: 'Direct cost reduction opportunities' },
    { id: 'm3', name: 'Long-Term Value Creation', categoryId: 'costSavings', weight: 25, description: 'Sustainable competitive advantage' },
    { id: 'm4', name: 'Financial Risk Factors', categoryId: 'costSavings', weight: 25, description: 'Investment risk considerations' },
  ],
  strategicAlignment: [
    { id: 'm5', name: 'Core Mission Support', categoryId: 'strategicAlignment', weight: 20, description: 'Alignment with organizational mission' },
    { id: 'm6', name: 'Competitive Differentiation', categoryId: 'strategicAlignment', weight: 10, description: 'Market positioning advantage' },
    { id: 'm7', name: 'Value-Based Care Integration', categoryId: 'strategicAlignment', weight: 10, description: 'Support for value-based models' },
    { id: 'm8', name: 'Strategic Growth', categoryId: 'strategicAlignment', weight: 20, description: 'Long-term viability and growth' },
    { id: 'm21', name: 'Operational Efficiency', categoryId: 'strategicAlignment', weight: 20, description: 'Process improvement potential' },
    { id: 'm22', name: 'Technology Alignment', categoryId: 'strategicAlignment', weight: 10, description: 'Fit with technology roadmap' },
    { id: 'm24', name: 'Revenue Diversification', categoryId: 'strategicAlignment', weight: 10, description: 'New revenue stream potential' },
  ],
  timeToValue: [
    { id: 'm51', name: 'Implementation Complexity', categoryId: 'timeToValue', weight: 17, description: 'Technical and operational complexity' },
    { id: 'm52', name: 'Market Readiness', categoryId: 'timeToValue', weight: 17, description: 'External market conditions' },
    { id: 'm53', name: 'Speed to Generate Returns', categoryId: 'timeToValue', weight: 17, description: 'Time to ROI realization' },
    { id: 'm54', name: 'Stakeholder Engagement', categoryId: 'timeToValue', weight: 17, description: 'Stakeholder buy-in requirements' },
    { id: 'm55', name: 'Pilot Feasibility', categoryId: 'timeToValue', weight: 16, description: 'Ability to run controlled pilots' },
    { id: 'm56', name: 'Regulatory Approval Speed', categoryId: 'timeToValue', weight: 16, description: 'Time for compliance approvals' },
  ],
  dataReadiness: [
    { id: 'm26', name: 'Data Quality', categoryId: 'dataReadiness', weight: 14, description: 'Completeness and accuracy of data' },
    { id: 'm27', name: 'Data Standardization', categoryId: 'dataReadiness', weight: 14, description: 'Consistency of data formats' },
    { id: 'm28', name: 'Data Accessibility', categoryId: 'dataReadiness', weight: 14, description: 'Ease of data access and integration' },
    { id: 'm29', name: 'Data Transformation', categoryId: 'dataReadiness', weight: 14, description: 'Cleansing requirements' },
    { id: 'm30', name: 'Interoperability', categoryId: 'dataReadiness', weight: 14, description: 'System connectivity' },
    { id: 'm31', name: 'Historical Data', categoryId: 'dataReadiness', weight: 15, description: 'Availability of historical records' },
    { id: 'm32', name: 'Real-Time Capabilities', categoryId: 'dataReadiness', weight: 15, description: 'Live data feed availability' },
  ],
  compliance: [
    { id: 'm34', name: 'Security & Privacy', categoryId: 'compliance', weight: 12, description: 'HIPAA and data security compliance' },
    { id: 'm35', name: 'De-Identification', categoryId: 'compliance', weight: 12, description: 'Anonymization requirements' },
    { id: 'm36', name: 'Regulatory Framework', categoryId: 'compliance', weight: 13, description: 'Alignment with regulations' },
    { id: 'm37', name: 'Legal Clarity', categoryId: 'compliance', weight: 13, description: 'Contractual requirements' },
    { id: 'm38', name: 'Auditability', categoryId: 'compliance', weight: 12, description: 'Reporting and audit requirements' },
    { id: 'm39', name: 'Vendor Compliance', categoryId: 'compliance', weight: 12, description: 'Third-party compliance' },
    { id: 'm40', name: 'Jurisdictional Complexity', categoryId: 'compliance', weight: 13, description: 'Multi-state/region considerations' },
    { id: 'm41', name: 'Patient Consent', categoryId: 'compliance', weight: 13, description: 'Consent management requirements' },
  ],
  orgReadiness: [
    { id: 'm42', name: 'Technical Infrastructure', categoryId: 'orgReadiness', weight: 15, description: 'IT systems and platforms' },
    { id: 'm43', name: 'Staff Expertise', categoryId: 'orgReadiness', weight: 15, description: 'Team skills and knowledge' },
    { id: 'm44', name: 'Organizational Processes', categoryId: 'orgReadiness', weight: 15, description: 'Workflow readiness' },
    { id: 'm45', name: 'Change Management', categoryId: 'orgReadiness', weight: 15, description: 'Adoption capacity' },
    { id: 'm46', name: 'Resource Allocation', categoryId: 'orgReadiness', weight: 25, description: 'Budget and staff availability' },
    { id: 'm47', name: 'Partnerships', categoryId: 'orgReadiness', weight: 15, description: 'External collaboration readiness' },
  ],
};

// ============================================================
// NORMALIZATION FUNCTIONS
// ============================================================

/**
 * Rebalances weights when one value changes, ensuring total = 100%
 * Uses proportional adjustment of other weights
 */
export function rebalanceCategoryWeights(
  currentWeights: CategoryWeightsMap,
  changedKey: string,
  newValue: number,
  minWeight = 5,
  maxWeight = 40
): CategoryWeightsMap {
  // Clamp the new value
  const clampedValue = Math.max(minWeight, Math.min(maxWeight, newValue));

  const oldValue = currentWeights[changedKey];
  const diff = clampedValue - oldValue;

  // Get other keys and their total
  const otherKeys = Object.keys(currentWeights).filter(k => k !== changedKey);
  const othersTotal = otherKeys.reduce((sum, k) => sum + currentWeights[k], 0);

  if (othersTotal === 0 || diff === 0) {
    return { ...currentWeights, [changedKey]: clampedValue };
  }

  const result: CategoryWeightsMap = { [changedKey]: clampedValue };
  let adjustedTotal = clampedValue;

  // Proportionally adjust others
  otherKeys.forEach((k, idx) => {
    const proportion = currentWeights[k] / othersTotal;
    let adjusted = currentWeights[k] - (diff * proportion);

    // Clamp to min/max
    adjusted = Math.max(minWeight, Math.min(maxWeight, adjusted));

    // For last item, ensure total = 100
    if (idx === otherKeys.length - 1) {
      adjusted = 100 - adjustedTotal;
      adjusted = Math.max(minWeight, Math.min(maxWeight, adjusted));
    }

    result[k] = Math.round(adjusted * 10) / 10; // Round to 1 decimal
    adjustedTotal += result[k];
  });

  // Final normalization pass to ensure exactly 100%
  return normalizeToTotal(result, 100);
}

/**
 * Normalizes weights to sum to a specific total (default 100)
 */
export function normalizeToTotal(
  weights: CategoryWeightsMap,
  targetTotal = 100
): CategoryWeightsMap {
  const currentTotal = Object.values(weights).reduce((sum, w) => sum + w, 0);

  if (currentTotal === 0) return weights;

  const factor = targetTotal / currentTotal;
  const scaledWeights = Object.entries(weights).reduce<CategoryWeightsMap>((acc, [key, value]) => {
    acc[key] = value * factor;
    return acc;
  }, {});

  return distributeRoundedWeights(scaledWeights, targetTotal);
}

/**
 * Rebalances measure weights within a category
 */
export function rebalanceMeasureWeights(
  currentWeights: Record<string, number>,
  changedKey: string,
  newValue: number,
  minWeight = 5,
  maxWeight = 50
): Record<string, number> {
  // Similar logic to category weights
  const clampedValue = Math.max(minWeight, Math.min(maxWeight, newValue));

  const oldValue = currentWeights[changedKey] || 0;
  const diff = clampedValue - oldValue;

  const otherKeys = Object.keys(currentWeights).filter(k => k !== changedKey);
  const othersTotal = otherKeys.reduce((sum, k) => sum + currentWeights[k], 0);

  if (othersTotal === 0 || diff === 0) {
    return { ...currentWeights, [changedKey]: clampedValue };
  }

  const result: Record<string, number> = { [changedKey]: clampedValue };
  let adjustedTotal = clampedValue;

  otherKeys.forEach((k, idx) => {
    const proportion = currentWeights[k] / othersTotal;
    let adjusted = currentWeights[k] - (diff * proportion);
    adjusted = Math.max(minWeight, Math.min(maxWeight, adjusted));

    if (idx === otherKeys.length - 1) {
      adjusted = 100 - adjustedTotal;
      adjusted = Math.max(minWeight, Math.min(maxWeight, adjusted));
    }

    result[k] = Math.round(adjusted * 10) / 10;
    adjustedTotal += result[k];
  });

  return normalizeToTotal(result, 100);
}

// ============================================================
// SCORING FUNCTIONS
// ============================================================

/**
 * Calculates a weighted score for an opportunity based on category weights
 * This is a simplified scoring for the prototype - production would use full measure scores
 * 
 * UPDATED: January 2026 - Golden Demo Integration
 * - ROI values are now in RAW NUMBERS (e.g., 5000000 = $5M)
 * - Previously assumed millions (e.g., 5 = $5M)
 * - Benchmark: $5,000,000 = score of 100
 */
export function calculateWeightedScore(
  opportunity: {
    roiEstimate: { low: number; high: number };
    implementationEffort: 'Low' | 'Medium' | 'High';
    timeToValue: string;
  },
  categoryWeights: CategoryWeightsMap
): number {
  // Proxy scores based on opportunity characteristics
  // In production, these would come from actual measure scores

  // Business Value dimension (costSavings + strategicAlignment + timeToValue)
  const avgRoi = (opportunity.roiEstimate.low + opportunity.roiEstimate.high) / 2;

  // UPDATED: ROI is now in raw numbers, not millions
  // $5,000,000 = 100 score (benchmark for "excellent" ROI)
  const ROI_BENCHMARK = 5000000;
  const roiScore = Math.min(100, (avgRoi / ROI_BENCHMARK) * 100);

  // Time to value score
  // Handle both "X-Y months" format and "X months" format
  const timeString = opportunity.timeToValue || '6';
  const months = parseInt(timeString.split('-')[0]) || parseInt(timeString) || 6;
  const timeScore = Math.max(0, 100 - (months * 8)); // Faster = higher

  // Implementation ease (inverted - lower effort = higher score)
  const effortScores = { Low: 90, Medium: 60, High: 30 };
  const effortScore = effortScores[opportunity.implementationEffort] || 60;

  // Calculate weighted score
  const bvWeight = (categoryWeights.costSavings || 25) +
    (categoryWeights.strategicAlignment || 20) +
    (categoryWeights.timeToValue || 10);
  const eoiWeight = (categoryWeights.dataReadiness || 15) +
    (categoryWeights.compliance || 15) +
    (categoryWeights.orgReadiness || 15);

  // BV score is average of ROI and strategic value
  const bvScore = roiScore;

  // EOI score is based on effort (proxy)
  const eoiScore = (effortScore + timeScore) / 2;

  // Combined score
  const totalWeight = bvWeight + eoiWeight;
  const weightedScore = ((bvScore * bvWeight) + (eoiScore * eoiWeight)) / totalWeight;

  return Math.round(weightedScore * 10) / 10;
}

/**
 * Converts category weights map to default format
 */
export function getDefaultCategoryWeightsMap(): CategoryWeightsMap {
  const result: CategoryWeightsMap = {};
  Object.entries(CATEGORY_DEFINITIONS).forEach(([key, def]) => {
    result[key] = def.defaultWeight;
  });
  return result;
}

/**
 * Converts measure weights to nested map format
 */
export function getDefaultMeasureWeightsMap(): MeasureWeightsMap {
  const result: MeasureWeightsMap = {};

  Object.entries(MEASURE_DEFINITIONS).forEach(([categoryId, measures]) => {
    result[categoryId] = measures.reduce((acc, measure) => {
      acc[measure.key] = measure.defaultWeight;
      return acc;
    }, {} as Record<string, number>);
  });

  return result;
}

/**
 * Check if category weights match defaults
 */
export function areCategoryWeightsDefault(weights: CategoryWeightsMap): boolean {
  const defaults = getDefaultCategoryWeightsMap();
  return Object.keys(defaults).every(key => weights[key] === defaults[key]);
}